import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PatientProgramStatusForm } from "../../../components/program-status/PatientProgramStatusForm";
import { workflowService } from "@/services/features/WorkflowService";
import { useAuth } from "@/contexts/AuthContext";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { Loader2 } from "lucide-react";
import { graphqlClient } from "@/services/core/graphqlClient";
import { GET_PROGRAM_STATUS_BY_ID } from "@/services/resources/workflowGquery";

const EditPatientProgramStatus = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, token, hasuraToken } = useAuth();
  const { handleError, handleSuccess } = useErrorHandler();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [patientProgramStatus, setPatientProgramStatus] = useState<{ statusName: string } | null>(null);

  useEffect(() => {
    const fetchProgramStatus = async () => {
      console.log('🔍 Fetching program status - ID:', id, 'Hasura Token exists:', !!hasuraToken);
      
      if (!id || !hasuraToken) {
        console.log('❌ Missing required data - ID:', id, 'Hasura Token:', !!hasuraToken);
        setLoading(false);
        return;
      }

      try {
        console.log('📡 Calling GraphQL: GET_PROGRAM_STATUS_BY_ID with ID:', id);
        const response = await graphqlClient.query(
          GET_PROGRAM_STATUS_BY_ID,
          { programStatusId: parseInt(id) },
          hasuraToken
        );
        
        console.log('✅ GraphQL response:', response);
        
        if (response.success && response.data?.pep_program_status?.[0]) {
          const statusData = response.data.pep_program_status[0];
          console.log('✅ Program status fetched successfully:', statusData);
          
          setPatientProgramStatus({
            statusName: statusData.program_status
          });
        } else {
          console.error('❌ No program status found');
          handleError(new Error("Program status not found"), "Failed to load program status");
        }
      } catch (error) {
        console.error('❌ Error fetching program status:', error);
        handleError(error, "Failed to load program status");
      } finally {
        setLoading(false);
      }
    };

    fetchProgramStatus();
  }, [id, hasuraToken, handleError]);

  const handleSubmit = async (data: any) => {
    if (!id || !user?.id || !token) {
      handleError(new Error("Missing required data"));
      return;
    }

    setIsSubmitting(true);
    try {
      await workflowService.updateProgramStatus(user.id, id, data, token);
      handleSuccess("Program status updated successfully");
      navigate("/admin/program-type", { state: { activeTab: "patient-program-status" } });
    } catch (error) {
      handleError(error, "Failed to update program status");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/program-type", { state: { activeTab: "patient-program-status" } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!patientProgramStatus) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">Patient Program Status Not Found</h2>
          <p className="text-muted-foreground mt-2">The requested patient program status could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <PatientProgramStatusForm
      initialData={patientProgramStatus}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isEdit={true}
      isSubmitting={isSubmitting}
    />
  );
};

export default EditPatientProgramStatus;